import React, { Component } from "react";
import {
  Menu,
  MenuItem,
  Button,
  Checkbox,
  ListItemText,
  TextField,
  Popover,
  Box,
  InputAdornment,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import SearchIcon from '@mui/icons-material/Search';
import Loader from "./Loader.web";
const configJSON = require("./config.js")

interface Item {
    name: string;
    subcategories: string[];
  }

interface MultiLevelDropdownProps {
  selectedItem: Item[];
  categories: string[]
  subCategories: string[]
  btnCustomStyle?: object;
  menuCustomStyle?: object;
  placeholder: string;
  isGettingSubCategories: boolean
  isAbleToSearch?: boolean;
  handleCategoryClick: (categoryName: string) => void
  setSelectedItems: (category: string, subcategory: string) => void;
}

interface MultiLevelDropdownState {
  anchorEl: HTMLElement | null;
  subMenuAnchorEl: HTMLElement | null;
  selectedCategory: string;
  searchTerm: string;
}

export default class MultiLevelDropdown extends Component<MultiLevelDropdownProps, MultiLevelDropdownState> {
  state: MultiLevelDropdownState = {
    anchorEl: null,
    subMenuAnchorEl: null,
    selectedCategory: "",
    searchTerm: "",
  };

  // Custom SVG icons
  CustomCheckedIcon = () => (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect width="18" height="18" rx="6" fill="#652784" />
      <path fill-rule="evenodd" clip-rule="evenodd" d="M13.5548 4.88024C13.9663 5.15916 14.0737 5.71879 13.7948 6.13022L9.21865 12.8802C9.0692 13.1007 8.8298 13.2436 8.56485 13.2706C8.29989 13.2975 8.0366 13.2058 7.8458 13.02L4.32191 9.58836C3.9658 9.24158 3.95824 8.67178 4.30502 8.31568C4.6518 7.95957 5.2216 7.95201 5.5777 8.29879L8.33174 10.9807L12.3049 5.12016C12.5838 4.70873 13.1434 4.60132 13.5548 4.88024Z" fill="white" />
    </svg>
  );

  CustomUncheckedIcon = () => (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="0.5" y="0.5" width="17" height="17" rx="5.5" stroke="#94A3B8" />
    </svg>
  );

  handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    this.setState({ anchorEl: event.currentTarget });
  };

  handleClose = () => {
    this.setState({ anchorEl: null, subMenuAnchorEl: null, selectedCategory: ""}, () => {
      this.handleSubMenuClose();
    });
  };

  handleSubMenuClick = (event: React.MouseEvent<HTMLElement>, category: string) => {
    if(this.state.subMenuAnchorEl) {
      this.setState({ selectedCategory: "", subMenuAnchorEl: null });
    } else {
      this.setState({ selectedCategory: category, subMenuAnchorEl: event.currentTarget });
      this.props.handleCategoryClick(category)
    }
  };

  handleSubMenuClose = () => {
    this.setState({ subMenuAnchorEl: null, selectedCategory: "" });
  };

  handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ searchTerm: event.target.value });
  };

  render() {
    const { 
        selectedItem, 
        categories,
        subCategories,
        placeholder, 
        btnCustomStyle,
        menuCustomStyle,
        isGettingSubCategories,
        setSelectedItems,
    } = this.props;

    const { 
        anchorEl, 
        subMenuAnchorEl, 
        selectedCategory, 
        searchTerm,
    } = this.state;

    const filteredCategories = categories.filter((cat) =>
      cat.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
      <Box>
        <StyledButton 
            onClick={this.handleOpen} 
            endIcon={<KeyboardArrowDownIcon sx={{ fill: "#94A3B8" }} />}
            sx={btnCustomStyle} 
        >
          {placeholder}
        </StyledButton>

        <StyledMenu
            disablePortal
            anchorEl={anchorEl} 
            open={Boolean(anchorEl)} 
            onClose={this.handleClose}
            sx={{ 
                "& .MuiPaper-root": {
                ...menuCustomStyle,
                "& ul": {
                  padding: "0px"
                }
                }
              }}
        >
          <SearchInput
            size="small"
            placeholder="Search"
            variant="outlined"
            value={searchTerm}
            onChange={this.handleSearchChange}
            onKeyDown={(event) => event.stopPropagation()}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ width: "16px", height: "16px", color: "#0D0C22" }} />
                </InputAdornment>
              ),
            }}
          />
          <StyledScroll>
            {filteredCategories.length > 0 ? (
              filteredCategories.map((category) => (
                <CustomMenuItem
                  key={category}
                  data-test-id="category-option"
                  isSelected={selectedCategory === category}
                  onClick={(event) => this.handleSubMenuClick(event, category)}
                >
                  {category}
                </CustomMenuItem>
              ))
            ) : (
              <CustomMenuItem disabled>
                {configJSON.noResultsFoundText}
              </CustomMenuItem>
            )}
          </StyledScroll>
        </StyledMenu>

        <StyledSubMenu
          disablePortal
          anchorEl={subMenuAnchorEl}
          open={Boolean(anchorEl) && Boolean(subMenuAnchorEl)}
          onClose={this.handleSubMenuClose}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
        >
          <StyledScroll sx={{ maxHeight: "268px" }}>
            <Loader loading={isGettingSubCategories} wrapperHeight={40}>
              {subCategories.length > 0 ? (
                subCategories.map((subcategory) => {
                  const isChecked = !!selectedItem.find((category) => 
                    category.name === selectedCategory && category.subcategories.includes(subcategory)
                  );
                  return (
                  <SubCatMenuItem
                    key={subcategory}
                    data-test-id="sub-category"
                    onClick={() => setSelectedItems(selectedCategory, subcategory)}
                  >
                    <Checkbox
                      checked={isChecked}
                      icon={<this.CustomUncheckedIcon />}
                      checkedIcon={<this.CustomCheckedIcon />}
                    />
                    <ListItemText primary={subcategory} />
                  </SubCatMenuItem>
                )})
              ) : (
                <SubCatMenuItem 
                  disabled
                  data-test-id="empty-sub-category"
                >
                  {configJSON.noResultsFoundText}
                </SubCatMenuItem>
              )}
            </Loader>
          </StyledScroll>
        </StyledSubMenu>
      </Box>
    );
  }
}

// Styled Components
const StyledButton = styled(Button)({
    background: "white",
    border: "1px solid #ccc",
    borderRadius: 8,
    padding: "8px 16px",
    minWidth: "150px",
    color: "#0F172A",
    display: "flex",
    justifyContent: "space-between",
    textTransform: "none",
    fontFamily: "Montserrat !important",
    fontSize: "14px !important",
    fontWeight: "500 !important",
    lineHeight: "22px !important",
    "&:hover": { background: "#f5f5f5" },
  });
  
  const StyledMenu = styled(Menu)({
    "& .MuiPaper-root": {
        borderRadius: 8,
        border: "1px solid #CBD5E1",
        boxShadow: "none",
        minWidth: "150px",
        boxSizing: "border-box",
        paddingRight: 0,
        margin: "4px 0px",
        overflowY: "hidden",
    },
  });

  const StyledScroll = styled(Box)({
    maxHeight: "215px",
    overflowY: "auto",
    overflowX: "hidden",
    "&::-webkit-scrollbar": {
      width: "5px", // ✅ Make scrollbar thin
    },
    "&::-webkit-scrollbar-thumb": {
      background: "#CBD5E1", // ✅ Light gray color
      borderRadius: "10px",
    },
    "&::-webkit-scrollbar-thumb:hover": {
      background: "#A1A1A1", // ✅ Slightly darker on hover
    },
    "&::-webkit-scrollbar-track": {
      background: "transparent", // ✅ Hide scrollbar track
    },
  });
  
  const StyledSubMenu = styled(Popover)({
    "& .MuiPaper-root": {
        borderRadius: 8,
        border: "1px solid #CBD5E1",
        boxShadow: "none",
        minWidth: "150px",
        boxSizing: "border-box",
        paddingRight: 0,
        margin: "4px",
        overflowY: "auto",
    },
  });
  
const SearchInput = styled(TextField)({
    margin: "8px",
    width: "calc(100% - 16px)",
    "& .MuiOutlinedInput-root": {
        borderRadius: 8,
        height: 36,
        fontSize: 14,
        '&.Mui-focused fieldset': {
            borderColor: '#64748B',
        },
    },
});

const CustomMenuItem = styled(MenuItem, {
  shouldForwardProp: (prop) => prop !== "isSelected",
})(({ isSelected }: { isSelected?: boolean }) => ({
  display: "flex",
  justifyContent: "space-between",
  minWidth: 150,
  padding: "8px 16px",
  fontFamily: "Montserrat !important",
  fontSize: "12px !important",
  fontWeight: "500 !important",
  lineHeight: "14.63px !important",
  pointerEvents: "auto",
  backgroundColor: isSelected ? "#EBC3FF" : "",
  "&:hover": {
    backgroundColor: "#EBC3FF",
  },
}));

const SubCatMenuItem = styled(MenuItem)({
  fontSize: "12px",
  fontWeight: "500",
  lineHeight: "14.63px",
  height: "36px",
  "&:hover": {
    backgroundColor: "#EBC3FF",
  },
})
